/**
 * Brand fonts (Brand Settings → Fonts): one for headings, one for everything
 * else, from a short list of Google Fonts that read well in a data-heavy app.
 * Stored on branding.layout.fonts = { heading, body } as the keys below; the
 * server keeps the same keys (server/utils/entityBranding.js BRAND_FONT_KEYS).
 * "default" means the layout style's own font.
 */
const gf = (family, weights = '400;500;600;700') => `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@${weights}&display=swap`;

export const BODY_FONTS = [
  { key: 'default', label: 'Layout default', family: null },
  { key: 'geist', label: 'Geist', family: 'Geist', css: gf('Geist') },
  { key: 'inter', label: 'Inter', family: 'Inter', css: gf('Inter') },
  { key: 'dm-sans', label: 'DM Sans', family: 'DM Sans', css: gf('DM Sans') },
  { key: 'manrope', label: 'Manrope', family: 'Manrope', css: gf('Manrope', '400;500;600;700;800') },
  { key: 'ibm-plex-sans', label: 'IBM Plex Sans', family: 'IBM Plex Sans', css: gf('IBM Plex Sans') },
  { key: 'work-sans', label: 'Work Sans', family: 'Work Sans', css: gf('Work Sans') },
  { key: 'source-sans-3', label: 'Source Sans 3', family: 'Source Sans 3', css: gf('Source Sans 3') },
  { key: 'lato', label: 'Lato', family: 'Lato', css: gf('Lato', '400;700') },
  { key: 'poppins', label: 'Poppins', family: 'Poppins', css: gf('Poppins') },
];

export const HEADING_FONTS = [
  { key: 'default', label: 'Same as body', family: null },
  { key: 'halant', label: 'Halant (serif)', family: 'Halant', css: gf('Halant', '400;500;600;700'), serif: true },
  { key: 'playfair-display', label: 'Playfair Display (serif)', family: 'Playfair Display', css: gf('Playfair Display'), serif: true },
  { key: 'fraunces', label: 'Fraunces (serif)', family: 'Fraunces', css: gf('Fraunces'), serif: true },
  { key: 'lora', label: 'Lora (serif)', family: 'Lora', css: gf('Lora'), serif: true },
  { key: 'dm-serif-display', label: 'DM Serif Display (serif)', family: 'DM Serif Display', css: gf('DM Serif Display', '400'), serif: true },
  { key: 'cormorant-garamond', label: 'Cormorant Garamond (serif)', family: 'Cormorant Garamond', css: gf('Cormorant Garamond'), serif: true },
  { key: 'space-grotesk', label: 'Space Grotesk', family: 'Space Grotesk', css: gf('Space Grotesk') },
  { key: 'sora', label: 'Sora', family: 'Sora', css: gf('Sora') },
  { key: 'outfit', label: 'Outfit', family: 'Outfit', css: gf('Outfit') },
  ...BODY_FONTS.filter((f) => f.key !== 'default'),
];

const SANS_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const SERIF_STACK = "Georgia, 'Times New Roman', serif";

export const findFont = (list, key) => list.find((f) => f.key === key) || list[0];

/** Load a font's stylesheet once (also used by the picker's previews). */
export function loadFont(font) {
  if (typeof document === 'undefined' || !font?.css) return;
  const id = `brand-fontfile-${font.key}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = font.css;
  document.head.appendChild(link);
}

/** CSS font-family value for a font, with a sensible fallback stack. */
export function fontStack(font) {
  if (!font?.family) return null;
  return `'${font.family}', ${font.serif ? SERIF_STACK : SANS_STACK}`;
}

/** Apply (or clear, with null) the brand's fonts on the whole app. */
export function applyBrandFonts(fonts) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const body = findFont(BODY_FONTS, fonts?.body);
  const heading = findFont(HEADING_FONTS, fonts?.heading);
  [['body', body], ['heading', heading]].forEach(([slot, font]) => {
    const stack = fontStack(font);
    if (stack) {
      loadFont(font);
      root.style.setProperty(`--brand-font-${slot}`, stack);
      root.classList.add(`brand-font-${slot}`);
    } else {
      root.style.removeProperty(`--brand-font-${slot}`);
      root.classList.remove(`brand-font-${slot}`);
    }
  });
}
